import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useFinance } from "./FinanceContext";
import { MONTHS } from "./data/seedData";
import { AnnualBar } from "./components/AnnualBar";
import { MonthInline } from "./components/MonthPicker";
import { KpiGrid, type KpiItem } from "./components/KpiGrid";
import { StatusBadge } from "./components/StatusChip";
import { toneColor } from "./components/tone";

export function MonthlyAnalysisPage() {
  const { model, fmt, scf, selMonth, openVouchers, locks } = useFinance();
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const its = model.items.filter((it) => !query || it.name.toLowerCase().includes(query));

  let tB = 0;
  let tA = 0;
  its.forEach((it) => {
    tB += (it.planned[selMonth] ?? 0) * scf;
    tA += it.actual[selMonth] ?? 0;
  });

  const trend = useMemo(
    () =>
      MONTHS.map((mn, i) => {
        let fb = 0;
        let fa = 0;
        let vb = 0;
        let va = 0;
        let db = 0;
        let da = 0;
        model.items.forEach((it) => {
          const b = (it.planned[i] ?? 0) * scf;
          const a = it.actual[i] ?? 0;
          if (it.type === "Fixed") {
            fb += b;
            fa += a;
          } else if (it.type === "Direct") {
            db += b;
            da += a;
          } else {
            vb += b;
            va += a;
          }
        });
        const tb = fb + vb + db;
        const ta = fa + va + da;
        return { mn, fb, fa, vb, va, db, da, tb, ta, v: tb - ta };
      }),
    [model, scf],
  );

  const ytd = trend.slice(0, selMonth + 1).reduce(
    (o, r) => ({
      fb: o.fb + r.fb,
      fa: o.fa + r.fa,
      vb: o.vb + r.vb,
      va: o.va + r.va,
      db: o.db + r.db,
      da: o.da + r.da,
      tb: o.tb + r.tb,
      ta: o.ta + r.ta,
    }),
    { fb: 0, fa: 0, vb: 0, va: 0, db: 0, da: 0, tb: 0, ta: 0 },
  );

  const kpis: KpiItem[] = [
    { label: `${MONTHS[selMonth]} Budget`, value: fmt(tB) },
    { label: `${MONTHS[selMonth]} Actual`, value: fmt(tA) },
    { label: "Variance", value: fmt(tB - tA), tone: tB - tA >= 0 ? "pos" : "neg" },
    { label: "Utilization", value: (tB ? (tA / tB) * 100 : 0).toFixed(1) + "%" },
  ];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Monthly Budget Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Full-year trend by cost type &amp; single-month detail{" "}
        {locks[selMonth] ? (
          <Chip size="small" label={`${MONTHS[selMonth]} Locked`} sx={{ ml: 1 }} />
        ) : null}
      </Typography>
      <AnnualBar />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>
        Monthly Trend — Budget vs Actual (Fixed / Variable / Direct)
      </Typography>
      <TableContainer sx={{ maxHeight: "50vh", mb: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell align="right">Fixed Bud</TableCell>
              <TableCell align="right">Fixed Act</TableCell>
              <TableCell align="right">Var Bud</TableCell>
              <TableCell align="right">Var Act</TableCell>
              <TableCell align="right">Dir Bud</TableCell>
              <TableCell align="right">Dir Act</TableCell>
              <TableCell align="right">Total Bud</TableCell>
              <TableCell align="right">Total Act</TableCell>
              <TableCell align="right">Variance</TableCell>
              <TableCell align="right">Var %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trend.map((r, i) => (
              <TableRow key={r.mn} sx={i === selMonth ? { bgcolor: "action.selected" } : undefined}>
                <TableCell sx={{ fontWeight: i === selMonth ? 700 : 400 }}>
                  {r.mn}
                  {i === selMonth ? " ◄" : ""}
                </TableCell>
                <TableCell align="right">{fmt(r.fb)}</TableCell>
                <TableCell align="right">{fmt(r.fa)}</TableCell>
                <TableCell align="right">{fmt(r.vb)}</TableCell>
                <TableCell align="right">{fmt(r.va)}</TableCell>
                <TableCell align="right">{fmt(r.db)}</TableCell>
                <TableCell align="right">{fmt(r.da)}</TableCell>
                <TableCell align="right">{fmt(r.tb)}</TableCell>
                <TableCell align="right">{fmt(r.ta)}</TableCell>
                <TableCell align="right" sx={{ color: toneColor(r.v >= 0 ? "pos" : "neg") }}>
                  {fmt(r.v)}
                </TableCell>
                <TableCell align="right">
                  {r.tb ? ((r.v / r.tb) * 100).toFixed(1) + "%" : "—"}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ "& td": { fontWeight: 800, borderTop: 2, borderColor: "divider" } }}>
              <TableCell>YTD Total (through {MONTHS[selMonth]})</TableCell>
              <TableCell align="right">{fmt(ytd.fb)}</TableCell>
              <TableCell align="right">{fmt(ytd.fa)}</TableCell>
              <TableCell align="right">{fmt(ytd.vb)}</TableCell>
              <TableCell align="right">{fmt(ytd.va)}</TableCell>
              <TableCell align="right">{fmt(ytd.db)}</TableCell>
              <TableCell align="right">{fmt(ytd.da)}</TableCell>
              <TableCell align="right">{fmt(ytd.tb)}</TableCell>
              <TableCell align="right">{fmt(ytd.ta)}</TableCell>
              <TableCell
                align="right"
                sx={{ color: toneColor(ytd.tb - ytd.ta >= 0 ? "pos" : "neg") }}
              >
                {fmt(ytd.tb - ytd.ta)}
              </TableCell>
              <TableCell align="right">
                {ytd.tb ? (((ytd.tb - ytd.ta) / ytd.tb) * 100).toFixed(1) + "%" : "—"}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Selected Month Detail — {MONTHS[selMonth]}
      </Typography>
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          label="Search cost head…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <MonthInline />
      </Stack>
      <KpiGrid items={kpis} />
      <TableContainer sx={{ maxHeight: "50vh" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Cost Head</TableCell>
              <TableCell>Department</TableCell>
              <TableCell align="right">Budget</TableCell>
              <TableCell align="right">Actual</TableCell>
              <TableCell align="right">Variance</TableCell>
              <TableCell align="right">Var %</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {its.map((it) => {
              const b = (it.planned[selMonth] ?? 0) * scf;
              const a = it.actual[selMonth] ?? 0;
              const v = b - a;
              const st = !b ? "g" : a / b > 1 ? "r" : a / b > 0.9 ? "a" : "g";
              return (
                <TableRow key={it.id}>
                  <TableCell>{it.code}</TableCell>
                  <TableCell>{it.name}</TableCell>
                  <TableCell>{it.dept}</TableCell>
                  <TableCell align="right">{fmt(b)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      cursor: "pointer",
                      color: "primary.main",
                      textDecoration: "underline dotted",
                    }}
                    onClick={() => openVouchers(it, "cm")}
                  >
                    {fmt(a)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: toneColor(v >= 0 ? "pos" : "neg") }}>
                    {fmt(v)}
                  </TableCell>
                  <TableCell align="right">{b ? ((v / b) * 100).toFixed(1) + "%" : "—"}</TableCell>
                  <TableCell align="right">
                    <StatusBadge status={st} />
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ "& td": { fontWeight: 800, borderTop: 2, borderColor: "divider" } }}>
              <TableCell colSpan={3}>TOTAL</TableCell>
              <TableCell align="right">{fmt(tB)}</TableCell>
              <TableCell align="right">{fmt(tA)}</TableCell>
              <TableCell align="right" sx={{ color: toneColor(tB - tA >= 0 ? "pos" : "neg") }}>
                {fmt(tB - tA)}
              </TableCell>
              <TableCell align="right">
                {tB ? (((tB - tA) / tB) * 100).toFixed(1) + "%" : "—"}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
